import { inject, observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { Button, Heading, Input, Paper, PasswordStrength, Text, Link } from "src/components";
import { theme } from "src/config";
import { getRoutePath, match, MatchParams, Page } from "src/routes";
import { AuthStore, RoutingStore, UserStore } from "src/stores";
interface IResetParams {
  token: string;
}

export interface IResetPasswordProps {
  authStore: AuthStore;
  userStore: UserStore;
  routingStore: RoutingStore;
  params?: IResetParams; // This is injected so it has to be called match
}

export const ResetPassword = inject(
  "authStore",
  "userStore",
  "routingStore"
)(
  observer((props: IResetPasswordProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const [token, setToken] = React.useState<string>("");
    const [password, setPassword] = React.useState<string>("");
    const [passwordRepeat, setPasswordRepeat] = React.useState<string>("");
    const [loading, setLoading] = React.useState<boolean>(false);
    const [valid, setValid] = React.useState<boolean>(false);

    const { requestResetPassword, isLoggedIn } = props.authStore;

    useEffect(() => {
      if (isLoggedIn) {
        props.routingStore.push(`/${Page.Home}`);
      }
    }, []);

    useEffect(() => {
      const { params } = props;
      setToken(params?.token);
    }, [props.params]);

    const submit = async () => {
      setLoading(true);
      const result = await requestResetPassword(token, password);
      setLoading(false);
      if (result == "success") {
        props.routingStore.push(`/${Page.Login}`);
        toast.success(t("password_reset_success"));
        toast.success(t("please_login_with_new_credentials"));
      } else {
        toast.success(t("password_reset_failed"));
      }
    };

    return (
      <Paper style={{ ...theme.paper.small, marginTop: "10vh", display: 'flex', flexDirection: 'column'}} $center>
        <Box gridRowGap={theme.layout.gutter} flexDirection='column' display='flex'>
          <Heading type="h3">{t("reset_password")}</Heading>
          <Heading type="h2">{t("enter_new_password")}</Heading>
          <Box>
            <hr />
          </Box>
          <Text>{t("new_password_requirements")}</Text>
          <Input
            type="password"
            label={t("password")}
            id="new-password"
            value={password}
            onChange={(value) => setPassword(value)}
            errorMessage={t("validation_error", { what: t("password") })}
          />
          <Input
            type="password"
            label={t("password_repeat")}
            id="repeat-password"
            value={passwordRepeat}
            onChange={(value) => setPasswordRepeat(value)}
            errorMessage={t("validation_error", { what: t("password") })}
          />
          <PasswordStrength
            password={password}
            passwordRepeat={passwordRepeat}
            onValidate={(v) => setValid(v)}
          />
          <Box justifyContent="right">
            <Button
              variant="primary"
              size='large'
              onClick={submit}
              isLoading={loading}
              disabled={!valid}
            >
              {t("send")}
            </Button>
          </Box>
          <Text size="small" as="p">
              {t("go_back_to_login")}{"? "}
            <Link
              size    = "small"
              text    = {t("click_here")}
              color   = {theme.color.primary7}
              onClick = {() =>
                props.routingStore.push(getRoutePath(Page.Login))
              }
            />
          </Text>
        </Box>
      </Paper>
    );
  })
);
